import { useCallback, useEffect, useRef, useState } from "react";

export const useVisibilityStatus = (options = {}) => {
  const {
    threshold = 1.0,
    rootMargin = '0px',
    trackTabVisibility = true,
    trackElementVisibility = true,
    onStatusChange = null,
    onElementVisibilityChange = null,
    onTabVisibilityChange = null
  } = options;

  const elementRef = useRef(null);
  const [isElementVisible, setIsElementVisible] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
  const [isOnline, setIsOnline] = useState(false);

  // Handle element visibility with Intersection Observer
  useEffect(() => {
    if (!trackElementVisibility || !elementRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsElementVisible(visible);
        onElementVisibilityChange?.(visible);
      },
      { threshold, rootMargin }
    );

    observer.observe(elementRef.current);
    return () => {
      observer.disconnect()
    };
  }, [threshold, rootMargin, trackElementVisibility, onElementVisibilityChange]);

  // Handle tab/page visibility changes
  useEffect(() => {
    if (!trackTabVisibility) return;

    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsTabVisible(visible);
      onTabVisibilityChange?.(visible);
    };

    const handleFocus = () => {
      setIsTabVisible(true);
      onTabVisibilityChange?.(true);
    };

    const handleBlur = () => {
      setIsTabVisible(false);
      onTabVisibilityChange?.(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [trackTabVisibility, onTabVisibilityChange]);

  // Calculate online status
  useEffect(() => {
    const newStatus = (trackElementVisibility ? isElementVisible : true) &&
      (trackTabVisibility ? isTabVisible : true);

    setIsOnline(newStatus);
    onStatusChange?.(newStatus, { isElementVisible, isTabVisible });
  }, [isElementVisible, isTabVisible, trackElementVisibility, trackTabVisibility, onStatusChange]);

  // Utility methods
  const forceOffline = useCallback(() => setIsOnline(false), []);
  const forceOnline = useCallback(() => setIsOnline(true), []);

  return {
    ref: elementRef,
    isOnline,
    isElementVisible,
    isTabVisible,
    forceOffline,
    forceOnline
  };
};
